import Bugsnag from '@bugsnag/js';
import { DermaQuestionsResponse } from '@interface/derma/dermaquestions';
import { AddCommentRequest, Status, User } from 'app/types/appointment';
import { Client } from 'app/types/client';

export default class UserService {
  static async getSimulationReady(id: string, clinicId: string) {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_CONTACTS_API}Crisalix/SimulationReady?id=${id}&clinic=${clinicId}`,
        {
          method: 'GET',
        }
      );
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return null;
      }
    } catch (err) {
      return false;
    }
  }

  static async createCrisalixUser(userId: string, clinicId: string) {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_CONTACTS_API}Crisalix/Patients?userId=${userId}&clinicId=${clinicId}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return null;
      }
    } catch (err) {
      return null;
    }
  }

  static async registerUser(formData: Client): Promise<User | undefined> {
    //if (isTruehair) formData.origin = 'truehair';

    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }

  static async getDermaQuestions(
    id: string
  ): Promise<DermaQuestionsResponse | undefined> {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Derma?userId=${id}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }

  static async getUserById(id: string) {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/${id}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return '';
      }
    } catch (err) {
      return '';
    }
  }

  static async getAccessToken(token: string): Promise<string> {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Mediquo/UserAccessToken?userToken=${token}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.text();
        return data;
      } else {
        Bugsnag.notify('Error getAccessToken' + res);
        return '';
      }
    } catch (err) {
      Bugsnag.notify('Error getAccessToken' + err);
      return '';
    }
  }

  static async getAllUsers(token: string): Promise<User[] | undefined> {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/All`;
      const res = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }

  static async blockCommunications(data: {
    userId: string;
    block: boolean;
  }): Promise<boolean> {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/BlockCommunications`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(data),
        }
      );

      console.log(res);

      if (res.ok) {
        return true;
      } else {
        return false;
      }
    } catch (err) {
      return false;
    }
  }
  static async createCallMeTask(formData: {
    phone: string;
    phonePrefix: string;
  }): Promise<any | undefined> {
    try {
      const res = await fetch(
        `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/CallMe`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        }
      );

      console.log(res);

      if (res.ok) {
        const data = await res;
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }
  static async getUserAppointments(userId: string, status?: Status[]) {
    try {
      const statusQuery = status?.length ? `&status=${status.join(',')}` : '';
      const url = `${process.env.NEXT_PUBLIC_SCHEDULE_API}appointment/PerUser?userid=${userId}${statusQuery}`;

      const res = await fetch(url, {
        method: 'GET',
        cache: 'no-store',
      });

      if (!res.ok) throw new Error('Failed to fetch');
      return await res.json();
    } catch (err) {
      console.error('Error fetching appointments:', err);
      return [];
    }
  }
  static async getUserComments(userId: string) {
    try {
      const url = `${process.env.NEXT_PUBLIC_PATIENTS_API}Comment?patientId=${userId}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return '';
      }
    } catch (err) {
      return '';
    }
  }
  static async deleteComment(id: string) {
    try {
      const url = `${process.env.NEXT_PUBLIC_PATIENTS_API}Comment/${id}`;
      const res = await fetch(url, { cache: 'no-store', method: 'DELETE' });
      if (res.ok) {
        return '';
      } else {
        return '';
      }
    } catch (err) {
      return '';
    }
  }

  // Recurrente = cita médica (con Beauty Pass) o estética terminada, la misma
  // regla que la etiqueta "Recurrente" de la agenda
  static async isRecurrent(userId: string): Promise<boolean> {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/IsRecurrent?userId=${userId}`;
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return false;
      return (await res.json()) === true;
    } catch (err) {
      return false;
    }
  }

  static async GetDashboardContact({
    search,
  }: {
    search: string;
  }): Promise<User | undefined> {
    try {
      const url = `${process.env.NEXT_PUBLIC_CONTACTS_API}Contact/GetContactDashboard?search=${search}`;
      // Sin Content-Type: en un GET no aporta nada y obliga a un preflight OPTIONS.
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }

  static async addComment(request: AddCommentRequest) {
    try {
      const formData = new FormData();
      formData.append('text', request.text);
      formData.append('patientId', request.patientId);
      formData.append('agentId', request.agentId);
      formData.append('taskId', request.taskId);
      formData.append('appointmentId', request.appointmentId);
      formData.append('professionalId', request.professionalId);

      if (request.dashboardJobId)
        formData.append('dashboardJobsId', request.dashboardJobId);

      formData.append('type', request.commentType.toString());
      request.files?.forEach((fileObj, index) => {
        formData.append(`Files[${index}].File`, fileObj.file);
        formData.append(`Files[${index}].Type`, fileObj.type.toString());
      });
      request.assignesInCopy?.forEach((assignee, index) => {
        formData.append(`AssignesInCopy[${index}]`, assignee);
      });
      /*request.requestFiles?.forEach((fileGroup, groupIndex) => {
        fileGroup.files.forEach(file => {
          formData.append(`RequestFiles[${groupIndex}].Files`, file);
          formData.append(
            `RequestFiles[${groupIndex}].Type`,
            fileGroup.type.toString()
          );
        });
      });*/
      const url = `${process.env.NEXT_PUBLIC_PATIENTS_API}Comment`;
      const res = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      if (res.ok) {
        const data = await res.json();
        return data;
      } else {
        return undefined;
      }
    } catch (err) {
      return undefined;
    }
  }
  static async sendBeautyPassWhatsapp(id: string) {
    const url = `${process.env.NEXT_PUBLIC_PATIENTS_API}BeautyPass/SendWhatsapp?id=${id}`;
    const res = await fetch(url, {
      method: 'POST',
    });
    if (res.ok) {
      return true;
    } else {
      Bugsnag.notify('Error sending beauty pass whatsapp ' + res);
      return false;
    }
  }

  static async sendAppointmentPdfWhatsapp(
    appointmentId: string,
    fileIds: string[]
  ) {
    try {
      const url = `${process.env.NEXT_PUBLIC_PATIENTS_API}AppointmentPdf/SendWhatsapp?id=${appointmentId}&fileIds=${fileIds.join(',')}`;
      const res = await fetch(url, {
        method: 'POST',
      });

      if (res.ok) {
        return true;
      } else {
        Bugsnag.notify('Error sending appointment pdf whatsapp ' + res);
        return false;
      }
    } catch (error: any) {
      Bugsnag.notify('Error sending appointment pdf whatsapp ' + error);
      return false;
    }
  }
}
